/**
 * CSV Export utility for de Partie.
 * Generates CSV strings and triggers download.
 */

const escapeCsv = (val) => {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildCsv = (headers, rows) => {
  const lines = [headers.map(escapeCsv).join(',')];
  rows.forEach((row) => {
    lines.push(row.map(escapeCsv).join(','));
  });
  return lines.join('\n');
};

const downloadCsv = (filename, csvContent) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportInventory = (inventory) => {
  const headers = ['Name', 'Quantity', 'Unit', 'Price', 'Servings', 'Expiry Date'];
  const rows = inventory.map((item) => [
    item.name,
    item.quantity,
    item.unit,
    item.price,
    item.servings,
    item.expiryDate || '',
  ]);
  downloadCsv('departie-inventory.csv', buildCsv(headers, rows));
};

export const exportRecipes = (recipes) => {
  const headers = ['Name', 'Servings', 'Tags', 'Calories', 'Protein (g)', 'Carbs (g)', 'Fat (g)', 'Ingredients', 'Procedure'];
  const rows = recipes.map((r) => [
    r.name,
    r.servings || 1,
    (r.tags || []).join('; '),
    r.macros?.calories || '',
    r.macros?.protein || '',
    r.macros?.carbs || '',
    r.macros?.fat || '',
    (r.ingredients || []).map((i) => `${i.quantity} ${i.unit} ${i.name}`).join('; '),
    (r.procedure || []).join('; '),
  ]);
  downloadCsv('departie-recipes.csv', buildCsv(headers, rows));
};

export const exportShoppingList = (shoppingList) => {
  const headers = ['Item', 'Quantity'];
  const rows = shoppingList.map((item) => [item.name, item.quantity]);
  downloadCsv('departie-shopping-list.csv', buildCsv(headers, rows));
};

export const exportBudgetExpenses = (expenses) => {
  const headers = ['Date', 'Description', 'Amount ($)', 'Category'];
  const rows = expenses.map((e) => [e.date, e.description, e.amount.toFixed(2), e.category || '']);
  downloadCsv('departie-budget-expenses.csv', buildCsv(headers, rows));
};

export const exportMealPlan = (mealPlan) => {
  const headers = ['Date', 'Meals'];
  const rows = Object.entries(mealPlan).map(([date, meals]) => [
    date,
    Array.isArray(meals) ? meals.join('; ') : String(meals),
  ]);
  downloadCsv('departie-meal-plan.csv', buildCsv(headers, rows));
};

export const exportMacroPlanner = (mealAssignments, recipes) => {
  const headers = ['Date', 'Meal Slot', 'Recipe', 'Calories', 'Protein (g)', 'Carbs (g)', 'Fat (g)'];
  const rows = Object.entries(mealAssignments).map(([key, recipeName]) => {
    const [date, slot] = key.split('__');
    const recipe = recipes.find((r) => r.name === recipeName);
    const m = recipe?.macros || {};
    return [date, slot, recipeName, m.calories || '', m.protein || '', m.carbs || '', m.fat || ''];
  });
  downloadCsv('departie-macro-plan.csv', buildCsv(headers, rows));
};

export const exportWorkoutLog = (workoutLog) => {
  const headers = ['Date', 'Exercise', 'Sets', 'Reps', 'Weight (lbs)', 'RPE', 'Day Name', 'Notes'];
  const rows = workoutLog.map((entry) => [
    entry.date,
    entry.exerciseName,
    entry.sets,
    entry.reps,
    entry.weight,
    entry.rpe,
    entry.dayName || '',
    entry.notes || '',
  ]);
  downloadCsv('departie-workout-log.csv', buildCsv(headers, rows));
};

export const exportFitnessLog = (fitnessLog) => {
  const headers = ['Date', 'Weight (lbs)', 'Body Fat (%)', 'Calories Consumed', 'Calories Burned', 'Steps', 'Notes'];
  const rows = fitnessLog.map((entry) => [
    entry.date,
    entry.weight || '',
    entry.bodyFat || '',
    entry.caloriesConsumed || '',
    entry.caloriesBurned || '',
    entry.steps || '',
    entry.notes || '',
  ]);
  downloadCsv('departie-fitness-log.csv', buildCsv(headers, rows));
};
